/**
 * Lightbox de la galería — Fase 10d.
 *
 * Modal grande con la foto activa, caption, autor + fecha, flechas
 * anterior/siguiente, botón cerrar y botón BORRAR si el caller es
 * autor o admin+. Navegación por teclado: Escape, flechas.
 */

import type { GalleryPhoto } from '@51esc/shared';

import './lightbox.css';

export interface LightboxOptions {
  photos: GalleryPhoto[];
  /** Índice inicial dentro de `photos`. */
  startIndex: number;
  /**
   * `true` si el usuario actual puede borrar esta foto (autor o admin).
   * El check se hace por foto a través del callback `canDelete`.
   */
  canDelete: (photo: GalleryPhoto) => boolean;
  /** Borrar foto; devuelve true si éxito. El lightbox la quita del array. */
  onDelete: (photoId: string) => Promise<boolean>;
  /** Aviso de error mostrado en la página tras cerrar el modal. */
  onError?: (message: string) => void;
}

interface ModalRefs {
  dialog: HTMLDialogElement;
  img: HTMLImageElement;
  caption: HTMLElement;
  meta: HTMLElement;
  prevBtn: HTMLButtonElement;
  nextBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
  deleteBtn: HTMLButtonElement;
  counter: HTMLElement;
}

let refs: ModalRefs | null = null;

export function openLightbox(opts: LightboxOptions): Promise<void> {
  const r = ensureModal();

  // Copia mutable del array porque el delete elimina elementos.
  const photos = [...opts.photos];
  let idx = Math.max(0, Math.min(opts.startIndex, photos.length - 1));

  const render = (): void => {
    if (photos.length === 0) {
      r.dialog.close();
      return;
    }
    if (idx >= photos.length) idx = photos.length - 1;
    const p = photos[idx]!;
    r.img.src = p.url;
    r.img.alt = p.caption ?? '';
    r.caption.textContent = p.caption ?? '';
    r.caption.hidden = !p.caption;
    r.meta.textContent = `${p.authorCallsign} · ${formatDate(p.createdAt)}`;
    r.counter.textContent = `${idx + 1} / ${photos.length}`;
    r.deleteBtn.hidden = !opts.canDelete(p);
    r.prevBtn.disabled = photos.length <= 1;
    r.nextBtn.disabled = photos.length <= 1;
  };

  return new Promise<void>((resolve) => {
    let resolved = false;
    const finish = (): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.prevBtn.removeEventListener('click', onPrev);
      r.nextBtn.removeEventListener('click', onNext);
      r.closeBtn.removeEventListener('click', onClose);
      r.deleteBtn.removeEventListener('click', onDelete);
      r.dialog.removeEventListener('cancel', onClose);
      r.dialog.removeEventListener('click', onBackdrop);
      document.removeEventListener('keydown', onKey);
      resolve();
    };
    const onClose = (ev?: Event): void => {
      ev?.preventDefault();
      finish();
    };
    const onBackdrop = (ev: MouseEvent): void => {
      // Click sobre el dialog (fuera del card) cierra.
      if (ev.target === r.dialog) finish();
    };
    const onPrev = (): void => {
      idx = (idx - 1 + photos.length) % photos.length;
      render();
    };
    const onNext = (): void => {
      idx = (idx + 1) % photos.length;
      render();
    };
    const onDelete = async (): Promise<void> => {
      const p = photos[idx];
      if (!p) return;
      r.deleteBtn.disabled = true;
      const ok = await opts.onDelete(p.id);
      r.deleteBtn.disabled = false;
      if (!ok) return;
      photos.splice(idx, 1);
      render();
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (!r.dialog.open) return;
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        onPrev();
      } else if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        onNext();
      }
    };

    r.prevBtn.addEventListener('click', onPrev);
    r.nextBtn.addEventListener('click', onNext);
    r.closeBtn.addEventListener('click', onClose);
    r.deleteBtn.addEventListener('click', () => void onDelete());
    r.dialog.addEventListener('cancel', onClose);
    r.dialog.addEventListener('click', onBackdrop);
    document.addEventListener('keydown', onKey);

    render();
    r.dialog.showModal();
  });
}

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'lb-dialog';
  dialog.innerHTML = `
    <div class="lb-card">
      <button type="button" class="lb-nav lb-nav--prev" aria-label="Anterior">◀</button>
      <div class="lb-frame">
        <img class="lb-img" alt="" />
        <div class="lb-info">
          <p class="lb-caption" hidden></p>
          <p class="lb-meta"></p>
          <p class="lb-counter"></p>
        </div>
      </div>
      <button type="button" class="lb-nav lb-nav--next" aria-label="Siguiente">▶</button>
      <div class="lb-actions">
        <button type="button" class="lb-btn lb-delete" hidden>Borrar</button>
        <button type="button" class="lb-btn lb-close">Cerrar</button>
      </div>
    </div>
  `;
  document.body.appendChild(dialog);

  const get = <T extends HTMLElement>(sel: string): T => {
    const el = dialog.querySelector<T>(sel);
    if (!el) throw new Error(`[lightbox] missing ${sel}`);
    return el;
  };
  refs = {
    dialog,
    img: get<HTMLImageElement>('.lb-img'),
    caption: get<HTMLElement>('.lb-caption'),
    meta: get<HTMLElement>('.lb-meta'),
    prevBtn: get<HTMLButtonElement>('.lb-nav--prev'),
    nextBtn: get<HTMLButtonElement>('.lb-nav--next'),
    closeBtn: get<HTMLButtonElement>('.lb-close'),
    deleteBtn: get<HTMLButtonElement>('.lb-delete'),
    counter: get<HTMLElement>('.lb-counter'),
  };
  return refs;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}
